import React, { useState } from 'react';
import { MonoLabel } from './Editorial';
import { useEntitlements } from '../context/EntitlementsContext';

const AVATAR_GRADIENT = 'linear-gradient(180deg, #4A5868, #2E3A48)';

function initialsFor(name) {
  if (!name) return '';
  const parts = name.trim().split(/\s+/);
  const first = parts[0]?.[0] || '';
  const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
  return (first + last).toUpperCase();
}

/* CoachAvatar — circular initials tile with a photo overlay (hides itself on
   load failure, falling back to the initials underneath). Used both in the
   match-detail coach rows and the CoachDetailModal header. */
export function CoachAvatar({ name, photoUrl, size = 44 }) {
  const [imgFailed, setImgFailed] = useState(false);
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        background: AVATAR_GRADIENT,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#fff',
        fontWeight: 700,
        fontSize: Math.round(size * 0.36),
        flexShrink: 0,
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      {initialsFor(name)}
      {photoUrl && !imgFailed && (
        <img
          src={photoUrl}
          alt=""
          onError={() => setImgFailed(true)}
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            borderRadius: '50%',
          }}
        />
      )}
    </div>
  );
}

// Inline 14x14 social glyphs — same convention as CoachPublicProfilePage.js.
const XIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="#1da1f2" aria-hidden="true">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);
const InstagramIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="#E1306C" aria-hidden="true">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
  </svg>
);
const CopyIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
    <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
  </svg>
);

function socialMeta(platform) {
  const p = (platform || '').toLowerCase();
  if (p === 'x' || p === 'twitter') return { label: 'X', icon: <XIcon />, base: 'https://x.com/' };
  if (p === 'instagram') return { label: 'Instagram', icon: <InstagramIcon />, base: 'https://instagram.com/' };
  return null;
}

export default function CoachDetailModal({ coach, schoolName, onClose }) {
  const [toast, setToast] = useState('');
  const { has } = useEntitlements();
  // Coach email/phone/socials/profile_url are Pro (coach_contact_info) —
  // the backend already nulls these fields for Free, so this gate is
  // belt-and-suspenders defense-in-depth, same reasoning as MatchDetailModal's
  // follow button. Gated strictly on the entitlement, never on the data: see
  // CoachesSection.jsx, whose identical hedge this copy reuses — the backend
  // nulls contact fields identically whether the athlete lacks the
  // entitlement OR the coach genuinely has none on record, so the client
  // cannot tell those two cases apart from the payload alone.
  // Unreachable today: COACH_CONTACT_INFO and SCHOOL_RESEARCH are both Pro, so
  // the coaches accordion is locked exactly when this gate would fire — a Free
  // athlete cannot open a coach to reach it. Kept as defense-in-depth against
  // the two features being split apart later.
  const canContact = has('coach_contact_info');

  if (!coach) return null;

  const copy = (value) => {
    if (!value) return;
    navigator.clipboard.writeText(value);
    setToast('Copied');
    setTimeout(() => setToast(''), 3000);
  };

  const dialablePhone = coach.phone && !coach.phone_needs_area_code ? coach.phone : null;
  const titleSchool = [coach.title, schoolName].filter(Boolean).join(' · ');
  const socials = (coach.socials || []).filter((s) => socialMeta(s.platform) && s.handle);
  const hasContact = Boolean(coach.email || coach.phone || socials.length > 0);

  return (
    <div className="modal-overlay" role="dialog" aria-modal="true" aria-labelledby="coach-detail-title" onClick={onClose}>
      <div className="modal-panel" style={{ maxWidth: 460 }} onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose} aria-label="Close">&times;</button>

        {toast && <div className="settings-toast" role="status">{toast}</div>}

        <div className="ed-row" style={{ gap: 14, alignItems: 'flex-start', paddingRight: 40 }}>
          <CoachAvatar name={coach.name} photoUrl={coach.photo_url} size={76} />
          <div style={{ minWidth: 0 }}>
            <h2 id="coach-detail-title" style={{ margin: 0, fontSize: 20, fontWeight: 700, letterSpacing: '-0.3px' }}>
              {coach.name}
            </h2>
            {titleSchool && (
              <div style={{ fontSize: 13, marginTop: 3, color: 'var(--ed-on-light-muted)' }}>{titleSchool}</div>
            )}
            <div className="ed-row" style={{ gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
              {coach.sport_label && (
                <span className="coach-detail__chip coach-detail__chip--sport">
                  {coach.sport_label}{coach.gender ? ` (${coach.gender})` : ''}
                </span>
              )}
              {coach.sport_role && (
                <span className="coach-detail__chip coach-detail__chip--role">
                  {coach.sport_role === 'secondary' ? 'Second sport' : 'Your sport'}
                </span>
              )}
            </div>
          </div>
        </div>

        {(hasContact || !canContact) && (
          <div style={{ marginTop: 20, borderTop: '1px solid #e2e8f0', paddingTop: 14 }}>
            <MonoLabel>Contact</MonoLabel>
            <div style={{ marginTop: 6 }}>
              {canContact ? (
                <>
                  {coach.email && (
                    <div className="coach-detail__row">
                      <a href={`mailto:${coach.email}`} className="coach-detail__value">{coach.email}</a>
                      <button type="button" className="coach-detail__copy" onClick={() => copy(coach.email)} aria-label="Copy email">
                        <CopyIcon />
                      </button>
                    </div>
                  )}
                  {coach.phone && (
                    <div className="coach-detail__row">
                      {dialablePhone ? (
                        <a href={`tel:${dialablePhone.replace(/\s/g, '')}`} className="coach-detail__value">{coach.phone}</a>
                      ) : (
                        <span className="coach-detail__value">{coach.phone}</span>
                      )}
                      <button type="button" className="coach-detail__copy" onClick={() => copy(coach.phone)} aria-label="Copy phone">
                        <CopyIcon />
                      </button>
                    </div>
                  )}
                  {socials.map((s) => {
                    const meta = socialMeta(s.platform);
                    const handle = s.handle.replace(/^@/, '');
                    const url = `${meta.base}${handle}`;
                    return (
                      <div className="coach-detail__row" key={s.platform}>
                        <a href={url} target="_blank" rel="noreferrer" className="coach-detail__value coach-detail__value--social">
                          {meta.icon}
                          <span>{meta.label}: {s.handle}</span>
                        </a>
                        <button type="button" className="coach-detail__copy" onClick={() => copy(s.handle)} aria-label={`Copy ${meta.label} handle`}>
                          <CopyIcon />
                        </button>
                      </div>
                    );
                  })}
                </>
              ) : (
                <p className="ed-muted coach-detail__contactGate" style={{ fontSize: 13 }}>
                  Coach contact info, when we have it, is part of Pro.
                </p>
              )}
            </div>
          </div>
        )}

        {coach.bio && (
          <div style={{ marginTop: 20, borderTop: '1px solid #e2e8f0', paddingTop: 14 }}>
            <MonoLabel>Bio</MonoLabel>
            <p style={{ marginTop: 8, fontSize: 13.5, lineHeight: 1.5 }}>{coach.bio}</p>
          </div>
        )}

        {canContact && coach.profile_url && (
          <div style={{ marginTop: 20 }}>
            <a href={coach.profile_url} target="_blank" rel="noreferrer">Staff directory page ↗</a>
          </div>
        )}
      </div>
    </div>
  );
}
